// src/components/ReferralTerms.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useNoIndex from "../hooks/useNoIndex";
import LoadingState from "./LoadingState";

const ReferralTerms = () => {
  useNoIndex();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch("/api/referral/config");
        const data = await response.json();
        setConfig(data);
      } catch (err) {
        console.error("Error fetching referral config:", err);
      }
    };
    fetchConfig();
  }, []);

  if (!config) {
    return <LoadingState />;
  }

  return (
    <div style={{
      maxWidth: "800px",
      margin: "40px auto",
      padding: "20px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    }}>
      <button
        onClick={() => navigate(-1)}
        style={{
          background: "transparent",
          border: "none",
          color: "#8B5FBF",
          fontSize: "14px",
          cursor: "pointer",
          marginBottom: "20px",
          textDecoration: "underline"
        }}
      >
        ← Back
      </button>

      <h1 style={{ fontSize: "32px", fontWeight: "700", marginBottom: "10px" }}>
        Referral Program Terms & Conditions
      </h1>
      <p style={{ color: "#666", marginBottom: "30px" }}>
        Last Updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
      </p>

      <div style={{ lineHeight: "1.8", color: "#333" }}>
        <h2 style={{ fontSize: "24px", fontWeight: "600", marginTop: "30px", marginBottom: "15px" }}>
          1. Program Overview
        </h2>
        <p>
          The OkGather Referral Program ("Program") allows existing members to invite friends to join OkGather and receive subscription credits as a reward. This Program is offered at OkGather's sole discretion and may be modified or terminated at any time.
        </p>

        <h2 style={{ fontSize: "24px", fontWeight: "600", marginTop: "30px", marginBottom: "15px" }}>
          2. Eligibility
        </h2>
        <ul style={{ paddingLeft: "20px" }}>
          <li>The Program is open to active OkGather members with a paid subscription in good standing.</li>
          <li>Each member receives one unique referral code.</li>
          <li>Members may share their referral code with friends who have not previously been OkGather members.</li>
        </ul>

        <h2 style={{ fontSize: "24px", fontWeight: "600", marginTop: "30px", marginBottom: "15px" }}>
          3. Referral Rewards
        </h2>

        <h3 style={{ fontSize: "18px", fontWeight: "600", marginTop: "20px", marginBottom: "10px" }}>
          For Referred Friends (New Members):
        </h3>
        <ul style={{ paddingLeft: "20px" }}>
          <li>
            Receive <strong>£{config.referredDiscount} off their first payment</strong> after completing the {config.trialDays}-day free trial
          </li>
          <li>Discount applied automatically when using a valid referral code at signup</li>
          <li>
            <strong>One-time discount only</strong> - applies only to the first paid month after trial
          </li>
          <li>Discount cannot be combined with other promotional offers</li>
        </ul>

        <h3 style={{ fontSize: "18px", fontWeight: "600", marginTop: "20px", marginBottom: "10px" }}>
          For Referrers (Existing Members):
        </h3>
        <ul style={{ paddingLeft: "20px" }}>
          <li>
            Earn <strong>£{config.referrerCreditPerMonth} per month in subscription credits for {config.durationMonths} consecutive months</strong> for each successful referral
          </li>
          <li>
            <strong>Limited-time offer:</strong> Credits are earned for a maximum of <strong>{config.durationMonths} months</strong> per referral, starting when the referred friend completes their first payment
          </li>
          <li>Credits automatically applied to your monthly subscription</li>
          <li>Maximum £{config.maxMonthlyCredit} credit can be applied per month (excess credits are banked for future months)</li>
          <li>Unlimited credit banking - unused credits roll over until used</li>
          <li>Credits are subscription discounts only and cannot be redeemed for cash</li>
        </ul>

        <h2 style={{ fontSize: "24px", fontWeight: "600", marginTop: "30px", marginBottom: "15px" }}>
          4. Credit Earning Period & Expiry
        </h2>
        <ul style={{ paddingLeft: "20px" }}>
          <li>
            <strong>Duration:</strong> Referrers earn credits for <strong>{config.durationMonths} months only</strong> per successful referral
          </li>
          <li>
            <strong>Start Date:</strong> Earning period begins when the referred friend completes their first payment (after the {config.trialDays}-day trial)
          </li>
          <li>
            <strong>End Date:</strong> Earning period ends exactly {config.durationMonths} months from the start date
          </li>
          <li>
            <strong>After Expiry:</strong> No additional credits earned from that referral after the {config.durationMonths}-month period
          </li>
          <li>
            <strong>Banked Credits:</strong> Credits already earned remain in your account and can be used indefinitely
          </li>
        </ul>

        <h2 style={{ fontSize: "24px", fontWeight: "600", marginTop: "30px", marginBottom: "15px" }}>
          5. Qualifying Referrals
        </h2>
        <p>A referral is considered successful when:</p>
        <ul style={{ paddingLeft: "20px" }}>
          <li>The referred friend uses a valid referral code at signup</li>
          <li>The referred friend completes the {config.trialDays}-day free trial</li>
          <li>The referred friend makes their first payment for a paid subscription</li>
          <li>The subscription remains active (not refunded or cancelled immediately)</li>
        </ul>

        <h2 style={{ fontSize: "24px", fontWeight: "600", marginTop: "30px", marginBottom: "15px" }}>
          6. Cancellations and Refunds
        </h2>
        <ul style={{ paddingLeft: "20px" }}>
          <li>
            If a referred friend cancels their subscription or requests a refund within 60 days of their first payment, referrer credits may be reversed
          </li>
          <li>Only unused/unapplied credits will be reversed</li>
          <li>Credits already applied to past invoices will not be reversed</li>
          <li>If a referrer cancels their subscription, all earned credits are forfeited and cannot be transferred</li>
        </ul>

        <h2 style={{ fontSize: "24px", fontWeight: "600", marginTop: "30px", marginBottom: "15px" }}>
          7. Prohibited Activities
        </h2>
        <p>The following activities are strictly prohibited:</p>
        <ul style={{ paddingLeft: "20px" }}>
          <li>Creating fake accounts or self-referrals</li>
          <li>Using bots or automated systems to generate referrals</li>
          <li>Spamming or unsolicited mass messaging</li>
          <li>Publicly posting referral codes on deal websites or forums without context</li>
          <li>Misrepresenting the Program or OkGather services</li>
          <li>Any fraudulent or deceptive practices</li>
        </ul>
        <p style={{ marginTop: "10px", color: "#ef4444", fontWeight: "500" }}>
          Violation of these terms may result in immediate termination from the Program, forfeiture of credits, and potential account suspension.
        </p>

        <h2 style={{ fontSize: "24px", fontWeight: "600", marginTop: "30px", marginBottom: "15px" }}>
          8. Credit Application
        </h2>
        <ul style={{ paddingLeft: "20px" }}>
          <li>Credits are applied automatically to your next subscription payment</li>
          <li>Credits reduce your subscription cost but cannot result in a negative balance or cash refund</li>
          <li>Credits are non-transferable and have no cash value</li>
          <li>If your subscription is cancelled, all unused credits are forfeited</li>
        </ul>

        <h2 style={{ fontSize: "24px", fontWeight: "600", marginTop: "30px", marginBottom: "15px" }}>
          9. Program Modifications
        </h2>
        <p>
          OkGather reserves the right to:
        </p>
        <ul style={{ paddingLeft: "20px" }}>
          <li>Modify, suspend, or terminate the Program at any time with or without notice</li>
          <li>Change reward amounts, earning periods, or eligibility criteria</li>
          <li>Disqualify participants who violate these terms</li>
          <li>Update these terms at any time (changes posted on this page)</li>
        </ul>
        <p style={{ marginTop: "10px" }}>
          Current participants will be notified of material changes that affect existing earned credits.
        </p>

        <h2 style={{ fontSize: "24px", fontWeight: "600", marginTop: "30px", marginBottom: "15px" }}>
          10. Limitation of Liability
        </h2>
        <p>
          OkGather is not responsible for:
        </p>
        <ul style={{ paddingLeft: "20px" }}>
          <li>Technical issues preventing referral tracking</li>
          <li>Delayed credit application due to payment processing issues</li>
          <li>Credits lost due to account closure or suspension</li>
          <li>Tax implications of received credits (consult a tax professional if needed)</li>
        </ul>

        <h2 style={{ fontSize: "24px", fontWeight: "600", marginTop: "30px", marginBottom: "15px" }}>
          11. Contact
        </h2>
        <p>
          For questions about the Referral Program, please contact us at:{" "}
          <a href="mailto:info@okgather.com" style={{ color: "#8B5FBF", textDecoration: "underline" }}>
            info@okgather.com
          </a>
        </p>

        <div style={{
          marginTop: "40px",
          padding: "20px",
          background: "#f9fafb",
          borderRadius: "8px",
          border: "1px solid #e5e7eb"
        }}>
          <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
            Summary
          </h3>
          <p style={{ fontSize: "14px", color: "#666" }}>
            <strong>For Referred Friends:</strong> £{config.referredDiscount} off first payment (one-time only)
          </p>
          <p style={{ fontSize: "14px", color: "#666", marginTop: "5px" }}>
            <strong>For Referrers:</strong> £{config.referrerCreditPerMonth}/month credit for {config.durationMonths} months per friend (max £{config.maxMonthlyCredit}/month applied, excess banked)
          </p>
          <p style={{ fontSize: "14px", color: "#666", marginTop: "5px", fontWeight: "500" }}>
            ⏰ <strong>Important:</strong> Credits earned for {config.durationMonths} months only per referral (limited-time offer)
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReferralTerms;
